import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { QualidadeCronogramaSGQForm, QualidadeCronogramaSGQSchema } from "../schemas/CronogramaSGQ/qualidade_acoes_melhorias";
import { CronogramaSGQLista } from "../../domain/models/cronogramaSGQ_lista";
import { useCronogramaSGQFilters } from "../hooks/Qualidade_cronogramaSGQ_lista/filter";
import { useCronogramaSGQPagination } from "../hooks/Qualidade_cronogramaSGQ_lista/pagination";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useParams } from "react-router";

type LoadCronogramaSGQListaListaFuncState = {
  IdCronogramaSGQ?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  status?: number,
  pesquisa?: string | number | Date,
};

export const useQualidadeCronogramaSGQListaViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    reports: { getReportCronogramaUseCase},
    qualidade: { getAllListagemCronogramaSGQUseCase, deleteCronogramaSGQUseCase, createCronogramaSGQUseCase, updateCronogramaSGQUseCase }
   
  } = useDependencyInjection();

   const {
      loadCentroCustoDropDown,
    } = useDropDownLoader();
  const changeHistory = useChangeHistory({
      getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
      onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

  const hookForm = useForm<QualidadeCronogramaSGQForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(QualidadeCronogramaSGQSchema),
    defaultValues: {
      ResponsavelCadastrado: false 
    }
  });

  const { status } = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [CronogramaSGQModal, setCronogramaSGQModal] = useState<boolean>(false);
  const [cronogramaSGQLista, setCronogramaSGQLista] = useState<CronogramaSGQLista[]>([])
  const [selectedCronogramaSGQ, setSelectedCronogramaSGQ] = useState<CronogramaSGQLista>();
  const [centroDeCustoLista, setCentroDeCustoLista] = useState<CentroCustoDropDown[]>([])

  //Arquivo
  const [cronogramaSGQListaDeleteModal, setCronogramaSGQListaDeleteModal] = useState<boolean>(false);

  const {pushErrorNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useCronogramaSGQFilters();
  const pagination = useCronogramaSGQPagination();

  //#region Relatorio
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion
  

  // #region Funções de carregamento

  const loadCronogramaSGQListaState = useCallback(async (funcState?: LoadCronogramaSGQListaListaFuncState) => {
      try {
        if(status !== undefined && status !== null) filters.status = Number(status);
        const filterID_CronogramaSGQ = evaluateParamValue(funcState?.IdCronogramaSGQ, filters.IdCronogramaSGQ);
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        const filteredStatus = funcState?.status ?? filters.status;
        await getAllListagemCronogramaSGQUseCase.getAllListagemCronogramaSGQ(
          {
            IdCronogramaSGQ: filterID_CronogramaSGQ,  PesquisarPor: filteredTipoPesquisa, Pesquisar: String(filteredPesquisa ), status: filteredStatus, 
          },
          filteredPage,
          filteredRowsPerPage
        );
        setCronogramaSGQLista(getAllListagemCronogramaSGQUseCase.items);
        pagination.setRowsPerPage(getAllListagemCronogramaSGQUseCase.pageSize);
        pagination.setTotalItems(getAllListagemCronogramaSGQUseCase.totalItems);
       
      } catch (e: unknown) {
          throw new Error("Falha ao CronogramaSGQ Lista!");
      }
    }, [filters, getAllListagemCronogramaSGQUseCase, pagination, status]);


  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if(Number(status) > 0) filters.setStatus(Number(status));
      await loadCentroCustoDropDown(list => setCentroDeCustoLista( list ));
      jobs.push(loadCronogramaSGQListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadCronogramaSGQListaState());
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadCronogramaSGQListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos

  const handleCronogramaSGQSubmit = useCallback(async (form: QualidadeCronogramaSGQForm) => {
      try {
        if(selectedCronogramaSGQ ){
          await updateCronogramaSGQUseCase.updateCronogramaSGQ(selectedCronogramaSGQ.IdCronogramaSGQ, form.Item,
            form.IdFuncionario,
            form.IdCentro_custo,
            form.Atividade ?? undefined,
            form.DescResponsavel ?? undefined,
            form.Observacao ?? undefined,
            form.ResponsavelCadastrado,
            form.DataPrevista,
            form.DataRealizada,
          form.DataPrevistaReal,
            form.DataRealizadaReal,
            form.ValorPrevisto,
            form.ValorRealizado,);
  
          setCronogramaSGQModal(false);
        }else{
           await createCronogramaSGQUseCase.createCronogramaSGQ(form.Item,
            form.IdFuncionario,
            form.IdCentro_custo,
            form.Atividade ?? undefined,
            form.DescResponsavel ?? undefined,
            form.Observacao ?? undefined,
            form.ResponsavelCadastrado,
            form.DataPrevista,
            form.DataRealizada,
            form.DataPrevistaReal,
            form.DataRealizadaReal,
            form.ValorPrevisto,
            form.ValorRealizado,
          );
    
          setCronogramaSGQModal(false);
        }
        await loadCronogramaSGQListaState();
      } catch (e) {
          pushErrorNotification("Erro", processError(e, 'Falha ao criar CronogramaSGQ!'));
      }finally {
        setSaving(false);
      }
    },[createCronogramaSGQUseCase, loadCronogramaSGQListaState, pushErrorNotification, selectedCronogramaSGQ, updateCronogramaSGQUseCase]);

  const handleCadastroCronogramaSGQOpenState = useCallback((IdCronogramaSGQ: number | null, openState: boolean) => {
      if (openState) {
        if (IdCronogramaSGQ) {
          setSelectedCronogramaSGQ(cronogramaSGQLista.find(x => x.IdCronogramaSGQ === IdCronogramaSGQ));
        } else {
          setSelectedCronogramaSGQ(undefined);
        }
      }
      setCronogramaSGQModal(openState);
    }, [cronogramaSGQLista]);
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadCronogramaSGQListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadCronogramaSGQListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
  
        await loadCronogramaSGQListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadCronogramaSGQListaState, pagination, pushErrorNotification]);

   const handleDeleteCronogramaSGQModalOpenState = useCallback((IdCronogramaSGQ: number | null, openState: boolean) => {
      if (openState) {
        if (IdCronogramaSGQ && cronogramaSGQLista) {
          setSelectedCronogramaSGQ(cronogramaSGQLista.find(x => x.IdCronogramaSGQ === IdCronogramaSGQ));
        } else {
          setSelectedCronogramaSGQ(undefined);
        }
      }
      setCronogramaSGQListaDeleteModal(openState);
    }, [cronogramaSGQLista]);
  

  const handleDeleteConfirmaCronogramaSGQ = useCallback(async () => {
    if (selectedCronogramaSGQ) {        try {
          setSaving(true);
          await deleteCronogramaSGQUseCase.deleteCronogramaSGQ(selectedCronogramaSGQ.IdCronogramaSGQ);
          await loadCronogramaSGQListaState();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
        } finally {
          setSaving(false);
          setCronogramaSGQListaDeleteModal(false);
        }
      }
    }, [deleteCronogramaSGQUseCase, loadCronogramaSGQListaState, pushErrorNotification, selectedCronogramaSGQ]);

  

  //#endregion



  //#region Filtros
  const handleClearFilters = useCallback(async () => {
      try {
          setLoading(true);
          filters.clearFilters();
          pagination.setPage(1);
          await loadCronogramaSGQListaState();
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar CronogramaSGQ!'));
      } finally {
          setLoading(false);
      }
  }, [filters, pagination, loadCronogramaSGQListaState, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: string | Date | number | null) => {
      useCronogramaSGQFilters.getState().setPesquisa(newValue);
      
      if (newValue) {
        return async () => {
          try {
            setLoading(true);
            useCronogramaSGQPagination.getState().setPage(1);
            await loadCronogramaSGQListaState({ pesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar CronogramaSGQ!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        return async () => {
          try {
            setLoading(true);
            useCronogramaSGQPagination.getState().setPage(1);
            await loadCronogramaSGQListaState(); 
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar CronogramaSGQ!"));
          } finally {
            setLoading(false);
          }
        }
      }
    }, 
    [ loadCronogramaSGQListaState, pushErrorNotification, setLoading]
  );
  
  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useCronogramaSGQFilters.getState().setTipoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useCronogramaSGQPagination.getState().setPage(1);
        await loadCronogramaSGQListaState({ tipoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar cronogramaSgq!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadCronogramaSGQListaState, pushErrorNotification]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
      useCronogramaSGQFilters.getState().setStatus(newValue);
      return async () => {
      try {
        setLoading(true);
        useCronogramaSGQPagination.getState().setPage(1);
        await loadCronogramaSGQListaState({ status: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar CronogramaSGQ!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadCronogramaSGQListaState, pushErrorNotification]);

  //#endregion
  
  //#region  Relatorio
  const handleReportModalOpenState = useCallback((openState: boolean) => {
    setOpenReportModal(openState);
  }, []);

  const handleSubmitReport = useCallback(async (form: QualidadeCronogramaSGQForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportCronogramaUseCase.getReportCronograma(
            requiredMimeType,
            form.DataPrevista ?? null,
            form.DataRealizada ?? null,
          ),
        closeReportModalCallback: () => {
          setOpenReportModal(false);
        }
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
    } finally {
      setLoading(false);
    }
  }, [getReportCronogramaUseCase, pushErrorNotification, startReportViewerProcess]);

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } 
    },
    [ changeHistory],
  );
  
  
  return {
    loading,
    saving,
    errorMessage,
    pagination,
    filters,
    cronogramaSGQLista,
    cronogramaSGQListaDeleteModal,
    selectedCronogramaSGQ,
    CronogramaSGQModal,
    centroDeCustoLista, 
    reportViewerState,
    openReportModal,
    hookForm,
    changeHistory,
    handleOpenChangeHistory,
    handleReload,
    handleRowsPerPageChange,
    handlePageChange,
    handleTipoPesquisaChange,
    handlePesquisaChange,
    handleClearFilters,
    handleStatusChange,
    handleDeleteCronogramaSGQModalOpenState, 
    handleDeleteConfirmaCronogramaSGQ,
    handleCadastroCronogramaSGQOpenState,
    handleCronogramaSGQSubmit,
    handleSubmitReport,
    handleReportModalOpenState
  }
}